import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react'
import { Filter, FilterInput } from '../FilterInput/index.js'
import { Sort, SortInput } from '../SortInput/index.js'
import { SearchInput } from '../SearchInput/index.js'
import { Menu } from '../Menu/index.js'
import { Text } from '../Text/index.js'
import { IconButton } from '../IconButton/index.js'
import {
  BasedUiEndpoints,
  useBasedEndpoints,
} from '../../providers/Endpoints/index.js'
import {
  BasedNode,
  UiSchema,
  UiSchemaProp,
  UiSchemaType,
} from '../../types/schema.js'
import {
  Table,
  TableProps,
  TableResetScroll,
  TablePagination,
  RowAction,
  BulkAction,
} from '../Table/index.js'
import { Stack } from '../Stack/index.js'
import { AppHeader } from '../AppHeader/index.js'
import { IconProps } from '../Icon/index.js'
import { capitalize, splitByUppercase } from '../../utils/text.js'
import {
  PageState,
  usePagedSubscription,
} from '../../hooks/usePagedSubscription.js'
import { useClient, useQuery } from '@based/sdk/react'
import { BasedClient } from '@based/sdk/client'
import { useFollowMe } from '../../hooks/useFollowMe.js'
import { useDependency } from '../../hooks/useDependency.js'
import { PartialByKeys } from '../../utils/common.js'
import useOverlay from '../../hooks/useOverlay.js'
import { Dialog } from '../Dialog/index.js'
import { Button } from '../Button/index.js'
import { radius } from '../../utils/radius.js'
import { colors } from '../../tokens/colors.js'
import { useLanguage } from '../LanguageSelect/index.js'

const EMPTY_NUM_SET = new Set<number>()
const EMPTY_STR_SET = new Set<string>()
const EMPTY_SCHEMA: UiSchema = { types: {} }
const DEFAULT_FILTER: Filter = { operator: 'and', items: [] }

export type BasedFinderProps = Omit<
  PartialByKeys<TableProps, 'schema'>,
  'data'
> & {
  variant?: 'regular' | 'modal'
  children?: ReactNode
  throttleDelay?: number
  bufferPageSize?: number
  customEndpoint?: string
  customEndpoints?: Partial<ReturnType<typeof useBasedEndpoints>>
  filter?: Filter
  onFilterChange?: React.Dispatch<React.SetStateAction<Filter>>
  search?: string
  onSearchChange?: React.Dispatch<React.SetStateAction<string>>
}

type BasedFinderContext = {
  client: BasedClient
  endpoints: Partial<BasedUiEndpoints>
  data: BasedNode | BasedNode[]
  checksum: number
  throttleDelay?: number
  schemaType: string
  schema: UiSchema
  variant: 'regular' | 'modal'
  rowHeight?: 'regular' | 'large'
  setRowHeight?: React.Dispatch<React.SetStateAction<'regular' | 'large'>>
  colVisibility?: 'fit' | 'scroll'
  setColVisibility?: React.Dispatch<React.SetStateAction<'fit' | 'scroll'>>
  colVisibilityOptions?: Record<'fit' | 'scroll', boolean>
  setColVisibilityOptions?: React.Dispatch<
    React.SetStateAction<Record<'fit' | 'scroll', boolean>>
  >
  customEndpoint?: string
  hasTrailButtonInHeaderRef?: React.MutableRefObject<boolean>
  disabledRows?: Set<number>
  colHidden?: Set<string>
  activeColumns?: UiSchemaType[]
  allColumns?: UiSchemaType[]
  availableColumns?: UiSchemaType[]
  filter?: Filter
  selected?: Set<number>
  maxSelectedRows?: number
  rowActions?: Record<string, RowAction> | Record<string, null>
  bulkActions?: Record<string, BulkAction> | Record<string, null>
  disabled?: boolean
  checkboxVisibility?: TableProps['checkboxVisibility']
  actionsVisibility?: TableProps['actionsVisibility']
  sort?: Sort
  search?: string | undefined
  selectableText?: boolean
  tableRef?: React.MutableRefObject<TableResetScroll | null>
  setColHidden?: React.Dispatch<React.SetStateAction<Set<string>>>
  resetView?: () => void
  resetData?: ({ pageSize, sort, filter, search }: Partial<PageState>) => void
  setActiveColumns?: React.Dispatch<React.SetStateAction<UiSchemaType[]>>
  setFilter?: React.Dispatch<React.SetStateAction<Filter>>
  onFilterChange?: React.Dispatch<React.SetStateAction<Filter>>
  setSort?: React.Dispatch<React.SetStateAction<Sort | undefined>>
  onSortChange?: React.Dispatch<React.SetStateAction<Sort>>
  onSearchChange?: React.Dispatch<React.SetStateAction<string>>
  setSearch?: React.Dispatch<React.SetStateAction<string | undefined>>
  onSelectedRowsChange?: (rows: Set<number>) => void
  setSelectedRows?: React.Dispatch<React.SetStateAction<Set<number>>>
  onRowClick?: (values: BasedNode, value: any) => void
  onRowSelect?: (index: number, values: BasedNode) => void
  onAddNewItem?: () => void
  pagination?: TablePagination
} | null

const BasedFinderContext = createContext<BasedFinderContext>(null)

function BasedFinder({
  variant = 'regular',
  children,
  schemaType,
  schema: schemaProp = EMPTY_SCHEMA,
  rowHeight: rowHeightProp,
  colVisibility: colVisibilityProp,
  rowActions,
  disabledRows: disabledRowsProp = EMPTY_NUM_SET,
  maxSelectedRows,
  bulkActions,
  customEndpoint,
  customEndpoints,
  disabled,
  bufferPageSize = 3,
  throttleDelay = 500,
  checkboxVisibility,
  actionsVisibility,
  selectedRows: selectedRowsProp,
  colHidden: colHiddenProp = EMPTY_STR_SET,
  sort: sortProp,
  filter: filterProp,
  search: searchProp,
  selectableText = false,
  onSortChange,
  onFilterChange,
  onSearchChange,
  onSelectedRowsChange,
  onRowClick,
  onRowSelect,
  onAddNewItem,
}: BasedFinderProps) {
  const client = useClient()
  const [lang] = useLanguage()
  const tableRef = useRef<TableResetScroll | null>(null)
  const hasTrailButtonInHeaderRef = useRef(false)
  const isRowHeightControlled = rowHeightProp !== undefined
  const isColVisibilityControlled = colVisibilityProp !== undefined
  const isSelectedRowsControlled = selectedRowsProp !== undefined
  const isColHiddenControlled = colHiddenProp !== undefined
  const isFilterControlled = filterProp !== undefined
  const isSearchControlled = searchProp !== undefined
  const isSortControlled = sortProp !== undefined

  const [internalSort, setInternalSort] = useState<Sort | undefined>(sortProp)
  const [internalFilter, setInternalFilter] = useState<Filter>(
    filterProp ?? DEFAULT_FILTER,
  )
  const [internalSearch, setInternalSearch] = useState<string | undefined>(
    searchProp,
  )
  const [internalSelectedRows, setInternalSelectedRows] = useState<Set<number>>(
    selectedRowsProp ?? EMPTY_NUM_SET,
  )
  const [internalRowHeight, setInternalRowHeight] = useState<
    'regular' | 'large'
  >(rowHeightProp ?? 'regular')
  const [internalColVisibility, setInternalColVisibility] = useState<
    'fit' | 'scroll'
  >(colVisibilityProp ?? 'fit')

  const [colVisibilityOptions, setColVisibilityOptions] = useState<
    Record<'fit' | 'scroll', boolean>
  >({ fit: true, scroll: true })

  const [activeColumns, setActiveColumns] = useState<UiSchemaType[]>([])
  const [pageSize, setPageSize] = useState<number | null>(null)
  const [checksumOffset, setChecksumOffset] = useState(0)
  const [internalColHidden, setInternalColHidden] =
    useState<Set<string>>(colHiddenProp)

  const sort = sortProp ?? internalSort
  const filter = filterProp ?? internalFilter
  const search = searchProp ?? internalSearch
  const selectedRows = selectedRowsProp ?? internalSelectedRows
  const rowHeight = (rowHeightProp ?? internalRowHeight) as 'regular' | 'large'
  const colVisibility = (colVisibilityProp ?? internalColVisibility) as
    | 'fit'
    | 'scroll'
  const colHidden = colHiddenProp ?? internalColHidden

  const setSort = useCallback(
    (next: React.SetStateAction<Sort | undefined>) => {
      if (!isSortControlled) setInternalSort(next)
    },
    [isSortControlled],
  )

  const setFilter = useCallback(
    (next: React.SetStateAction<Filter>) => {
      if (!isFilterControlled) setInternalFilter(next)
    },
    [isFilterControlled],
  )

  const setSearch = useCallback(
    (next: React.SetStateAction<string | undefined>) => {
      if (!isSearchControlled) setInternalSearch(next)
    },
    [isSearchControlled],
  )

  const setSelectedRows = useCallback(
    (next: React.SetStateAction<Set<number>>) => {
      if (!isSelectedRowsControlled) setInternalSelectedRows(next)
    },
    [isSelectedRowsControlled],
  )

  const setRowHeight = useCallback(
    (next: React.SetStateAction<'regular' | 'large'>) => {
      if (!isRowHeightControlled) setInternalRowHeight(next)
    },
    [isRowHeightControlled],
  )

  const setColVisibility = useCallback(
    (next: React.SetStateAction<'fit' | 'scroll'>) => {
      if (!isColVisibilityControlled) setInternalColVisibility(next)
    },
    [isColVisibilityControlled],
  )

  const setColHidden = useCallback(
    (next: React.SetStateAction<Set<string>>) => {
      if (!isColHiddenControlled) setInternalColHidden(next)
    },
    [isColHiddenControlled],
  )

  const endpoints = useBasedEndpoints()

  const mergedEndpoints = useMemo(() => {
    if (!customEndpoints) return endpoints
    return {
      ...endpoints,
      ...customEndpoints,
    }
  }, [useDependency(endpoints), useDependency(customEndpoints)])

  const { data: schemaData, checksum: schemaChecksum } = useQuery(
    mergedEndpoints.schema,
  )

  const schema = useMemo(() => {
    if (Object.keys(schemaProp?.types || {}).length) return schemaProp
    if (Object.keys(schemaData?.types || {}).length) return schemaData
    return schemaProp
  }, [useDependency(schemaProp), useDependency(schemaData)])

  const checksum = schemaChecksum + checksumOffset

  const typeSchemaRaw: UiSchemaType = useMemo(
    () =>
      schema?.[schemaType || '_root'] ||
      schema?.types?.[schemaType || '']?.props ||
      {},
    [useDependency(schema), schemaType],
  )

  const typeSchema: string[] = useMemo(
    () =>
      Object.entries(typeSchemaRaw)
        .filter(
          ([_key, value]: [string, UiSchemaProp]) =>
            Boolean(value) || value?.hide === true,
        )
        .map(([key]) => key),
    [useDependency(typeSchemaRaw)],
  )

  const subscribedData = usePagedSubscription({
    endpoint: mergedEndpoints.query,
    schemaType,
    bufferPageSize,
    include: typeSchema,
    lang,
  })

  const {
    total: subscribedTotal,
    data: subscribedRows,
    getPage,
    resetData: resetSub,
  } = subscribedData

  const forcedHiddenRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (isColHiddenControlled) return

    setInternalColHidden((prev) => {
      let changed = false
      const next = new Set(prev)

      for (const key of Object.keys(typeSchemaRaw)) {
        const hide = typeSchemaRaw?.[key]?.hide
        if (hide === true) {
          if (!forcedHiddenRef.current.has(key))
            forcedHiddenRef.current.add(key)
          if (!next.has(key)) {
            next.add(key)
            changed = true
          }
        } else if (hide === false) {
          if (forcedHiddenRef.current.has(key)) {
            forcedHiddenRef.current.delete(key)
            if (next.has(key)) {
              next.delete(key)
              changed = true
            }
          }
        }
      }

      return changed ? next : prev
    })
  }, [useDependency(typeSchemaRaw), isColHiddenControlled])

  const pagination: TablePagination = useMemo(
    () => ({
      total: subscribedTotal,
      type: 'scroll',
      onPageLoad: ({ pageSize }) => {
        setPageSize((prev) => (prev === pageSize ? prev : pageSize))
      },
      onPageChange: async ({ index, pageSize: internalPageSize }) => {
        getPage({
          pageIndex: index,
          pageSize: pageSize || internalPageSize,
          sort,
          filter,
          search,
        })
      },
      onPreloadPage: ({ index, pageSize: internalPageSize }) => {
        getPage({
          pageIndex: index,
          pageSize: pageSize || internalPageSize,
          sort,
          filter,
          search,
        })
      },
      preloadRatio: 0.01,
    }),
    [
      subscribedTotal,
      getPage,
      pageSize,
      useDependency(sort),
      useDependency(filter),
      search,
    ],
  )

  const sortDep = useDependency(sort)
  const filterDep = useDependency(filter)
  const resetSigRef = useRef<string>('')

  useEffect(() => {
    if (pageSize == null) return

    const sig = `${schemaType}|${sortDep}|${filterDep}|${search ?? ''}|${pageSize}`
    if (resetSigRef.current === sig) return
    resetSigRef.current = sig

    resetSub({ pageSize, sort, filter, search })
    tableRef.current?.resetScroll?.()

    if (selectedRows.size) {
      const cleared = new Set<number>()
      onSelectedRowsChange?.(cleared)
      setSelectedRows(cleared)
    }
  }, [
    schemaType,
    sortDep,
    filterDep,
    search,
    pageSize,
    resetSub,
    setSelectedRows,
    onSelectedRowsChange,
    selectedRows.size,
  ])

  const resetData = useCallback(() => {
    if (!isSortControlled) setInternalSort(undefined)
    if (!isSelectedRowsControlled) setInternalSelectedRows(new Set())
    setChecksumOffset((prev) => prev + 1)
    resetSub({ pageSize })
  }, [resetSub, pageSize, isSortControlled, isSelectedRowsControlled])

  const resetView = useCallback(() => {
    setRowHeight('regular')

    setColVisibility(colVisibilityOptions.fit ? 'fit' : 'scroll')

    setSearch(undefined)
    setFilter(DEFAULT_FILTER)

    resetData()
  }, [
    colVisibilityOptions.fit,
    setRowHeight,
    setColVisibility,
    setSearch,
    setFilter,
    resetData,
  ])

  const allColumns = useMemo(() => {
    const withId = typeSchema.includes('id')
      ? typeSchema
      : ['id', ...typeSchema]
    return withId.map((key) => {
      return {
        key,
        prop: typeSchemaRaw[key],
      } as unknown as UiSchemaType
    }) as UiSchemaType[]
  }, [useDependency(typeSchemaRaw)])

  const availableColumns = useMemo(() => {
    return activeColumns.filter(
      ({ key }) => key !== 'id' && !colHidden.has(key),
    )
  }, [activeColumns, colHidden])

  const [followMe, setFollowMe] = useFollowMe()

  const lastFollowersStateRef = useRef<'none' | 'some'>('none')
  useEffect(() => {
    const hasFollowers =
      Array.isArray(followMe?.followers) && followMe.followers.length > 0

    if (!hasFollowers) {
      if (lastFollowersStateRef.current !== 'none') {
        lastFollowersStateRef.current = 'none'
        setFollowMe({ finder: {} })
      }
      return
    }

    lastFollowersStateRef.current = 'some'
  }, [useDependency(followMe?.followers), setFollowMe])

  const followSig = useMemo(() => {
    return [
      [...selectedRows].join(','),
      JSON.stringify(sort ?? null),
      JSON.stringify(filter ?? null),
      String(search ?? ''),
      rowHeight,
      colVisibility,
      [...colHidden].join(','),
    ].join('|')
  }, [
    useDependency(selectedRows),
    useDependency(sort),
    useDependency(filter),
    search,
    rowHeight,
    colVisibility,
    useDependency(colHidden),
  ])

  const prevFollowSigRef = useRef<string>('')

  useEffect(() => {
    const hasFollowers =
      Array.isArray(followMe?.followers) && followMe.followers.length > 0
    if (!hasFollowers) return

    if (prevFollowSigRef.current === followSig) return
    prevFollowSigRef.current = followSig

    setFollowMe({
      finder: {
        selected: [...selectedRows],
        sort,
        filter,
        search,
        rowHeight,
        colVisibility,
        colHidden: [...colHidden],
      },
    })
  }, [
    followSig,
    followMe?.followers,
    setFollowMe,
    selectedRows,
    sort,
    filter,
    search,
    rowHeight,
    colVisibility,
    colHidden,
  ])

  const followingId = followMe?.following?.id
  const followingFinderDep = useDependency(followMe?.following?.finder)

  useEffect(() => {
    if (followingId === undefined) return
    const finder = followMe?.following?.finder
    if (!finder) return

    setSelectedRows(new Set(finder.selected || []))
    setSort(finder.sort)
    setFilter(finder.filter)
    setSearch(finder.search)
    setRowHeight(finder.rowHeight)
    setColVisibility(finder.colVisibility)
    setColHidden(new Set(finder.colHidden || []))
  }, [
    followingId,
    followingFinderDep,
    setSelectedRows,
    setSort,
    setFilter,
    setSearch,
    setRowHeight,
    setColVisibility,
    setColHidden,
    followMe,
  ])

  const [genericDialogPayload, setGenericDialogPayload] = useState<{
    title?: string
    description?: string
    result?: boolean
    callback?: () => void
    callToAction?: string
  }>()

  const genericDialog = useOverlay(Dialog, {
    title: genericDialogPayload?.title ?? '',
    description: genericDialogPayload?.description ?? '',
    children: (
      <Stack gap={16}>
        <Button
          keyHint="Esc"
          variant="border"
          onClick={() => {
            Dialog.Close()
          }}
          width="full"
          align="center"
        >
          Cancel
        </Button>
        <Button
          keyHint="Enter"
          color={
            genericDialogPayload?.callToAction?.toLowerCase() === 'delete'
              ? 'red'
              : 'neutral'
          }
          width="full"
          align="center"
          onClick={() => {
            setGenericDialogPayload((prev) => ({ ...prev, result: true }))
            Dialog.Close()
          }}
        >
          {genericDialogPayload?.callToAction ?? ''}
        </Button>
      </Stack>
    ),
  })

  useEffect(() => {
    if (
      genericDialogPayload?.title &&
      genericDialogPayload?.description &&
      !genericDialogPayload?.result
    ) {
      genericDialog()
    }

    if (
      genericDialogPayload?.result === true &&
      genericDialogPayload?.callback
    ) {
      genericDialogPayload.callback()
    }
  }, [
    genericDialogPayload?.title,
    genericDialogPayload?.description,
    genericDialogPayload?.result,
    genericDialogPayload?.callback,
  ])

  const defaultRowActions = useMemo(
    () =>
      ({
        ...rowActions,
        duplicate:
          (rowActions?.duplicate !== null && {
            label: `Duplicate`,
            leadIcon: 'copy',
            ...rowActions?.duplicate,
            onClick: async (id: number) => {
              if (rowActions?.duplicate?.onClick) {
                rowActions?.duplicate?.onClick?.(id)
                resetData()
              } else {
                await client.call(mergedEndpoints.copy, {
                  type: schemaType,
                  id,
                })
                resetData()
              }
            },
          }) ||
          null,
        delete:
          (rowActions?.delete !== null && {
            label: `Delete`,
            leadIcon: 'delete',
            color: 'red',
            ...rowActions?.delete,
            onClick: (id: number) => {
              setGenericDialogPayload({
                callback: async () => {
                  if (rowActions?.delete?.onClick) {
                    rowActions?.delete?.onClick?.(id)
                    resetData()
                  } else {
                    setGenericDialogPayload({})
                    await client.call(mergedEndpoints.delete, {
                      type: schemaType,
                      id,
                    })
                    resetData()
                  }
                },
                title: 'Delete item?',
                description:
                  'This item will be permanently deleted. This action cannot be undone.',
                callToAction: 'Delete',
              })
            },
          }) ||
          null,
      }) as Record<string, RowAction>,
    [useDependency(rowActions), resetData, client, mergedEndpoints, schemaType],
  )

  const defaultBulkActions = useMemo(
    () =>
      ({
        ...bulkActions,
        duplicate:
          (bulkActions?.duplicate !== null && {
            label: `Duplicate ${selectedRows?.size} items`,
            leadIcon: 'copy',
            ...bulkActions?.duplicate,
            onClick: async (ids: Set<number>) => {
              setGenericDialogPayload({
                callback: async () => {
                  if (bulkActions?.duplicate?.onClick) {
                    bulkActions?.duplicate?.onClick?.(ids)
                    resetData()
                  } else {
                    await Promise.all(
                      [...ids].map((id) =>
                        client.call(mergedEndpoints.copy, {
                          type: schemaType,
                          id,
                        }),
                      ),
                    )
                    resetData()
                  }
                },
                title: `Duplicate ${selectedRows?.size} items?`,
                description:
                  'Duplicating these items might take a while and affect other users on this workspace.',
                callToAction: 'Duplicate',
              })
            },
          }) ||
          null,
        delete:
          (bulkActions?.delete !== null && {
            label: `Delete ${selectedRows?.size} items`,
            leadIcon: 'delete',
            color: 'red',
            ...bulkActions?.delete,
            onClick: async (ids: Set<number>) => {
              setGenericDialogPayload({
                callback: async () => {
                  if (bulkActions?.delete?.onClick) {
                    bulkActions?.delete?.onClick?.(ids)
                    resetData()
                  } else {
                    setGenericDialogPayload({})
                    await Promise.all(
                      [...ids].map((id) =>
                        client.call(mergedEndpoints.delete, {
                          type: schemaType,
                          id,
                        }),
                      ),
                    )
                    resetData()
                  }
                },
                title: `Delete ${selectedRows?.size} items?`,
                description:
                  'These items will be permanently deleted. This action cannot be undone.',
                callToAction: 'Delete',
              })
            },
          }) ||
          null,
      }) as Record<string, BulkAction>,
    [
      useDependency(bulkActions),
      useDependency(selectedRows),
      resetData,
      client,
      mergedEndpoints,
      schemaType,
    ],
  )

  const ctx = useMemo(
    () => ({
      client,
      endpoints: mergedEndpoints,
      checksum,
      schema,
      schemaType,
      variant,
      filter,
      sort,
      search,
      selected: selectedRows,
      disabledRows: disabledRowsProp,
      maxSelectedRows,
      data: subscribedRows,
      hasTrailButtonInHeaderRef,
      rowHeight,
      colHidden,
      colVisibility,
      throttleDelay,
      checkboxVisibility,
      actionsVisibility,
      rowActions: defaultRowActions,
      bulkActions: defaultBulkActions,
      customEndpoint,
      activeColumns,
      allColumns,
      availableColumns,
      disabled,
      selectableText,
      tableRef,
      colVisibilityOptions,
      setColVisibilityOptions,
      setRowHeight,
      setColVisibility,
      setColHidden,
      setActiveColumns,
      setSort,
      setFilter,
      setSearch,
      setSelectedRows,
      resetView,
      resetData: resetSub,
      onSortChange,
      onFilterChange,
      onSearchChange,
      onSelectedRowsChange,
      onRowClick,
      onRowSelect,
      onAddNewItem,
      pagination,
    }),
    [
      client,
      mergedEndpoints,
      checksum,
      schema,
      schemaType,
      variant,
      useDependency(filter),
      useDependency(sort),
      search,
      useDependency(selectedRows),
      disabledRowsProp,
      maxSelectedRows,
      subscribedRows,
      rowHeight,
      useDependency(colHidden),
      colVisibility,
      throttleDelay,
      checkboxVisibility,
      actionsVisibility,
      defaultRowActions,
      defaultBulkActions,
      customEndpoint,
      activeColumns,
      allColumns,
      availableColumns,
      disabled,
      selectableText,
      colVisibilityOptions,
      setRowHeight,
      setColVisibility,
      setColHidden,
      setActiveColumns,
      setSort,
      setFilter,
      setSearch,
      setSelectedRows,
      resetView,
      resetSub,
      onSortChange,
      onFilterChange,
      onSearchChange,
      onSelectedRowsChange,
      onRowClick,
      onRowSelect,
      onAddNewItem,
      pagination,
    ],
  )

  if (!schema) return null

  return (
    <BasedFinderContext.Provider value={ctx}>
      <Stack
        direction="column"
        style={{
          height: '100%',
          border:
            variant === 'regular' ? `none` : `1px solid ${colors.neutral10}`,
          borderRadius: radius[20],
        }}
      >
        {children ? children : <BasedFinder.Body />}
      </Stack>
    </BasedFinderContext.Provider>
  )
}

export type BasedFinderHeaderProps = {
  title?: string
  icon?: IconProps['variant']
  leadButton?: ReactNode
  trailButton?: ReactNode
  filterButton?: boolean
  sortButton?: boolean
  searchButton?: boolean
  viewSettingsButton?: boolean
}

const BasedFinderHeader: React.FC<BasedFinderHeaderProps> = ({
  title,
  icon,
  leadButton,
  trailButton,
  filterButton: filterButtonProp = true,
  sortButton: sortButtonProp = true,
  searchButton: searchButtonProp = true,
  viewSettingsButton: viewSettingsButtonProp = true,
}) => {
  const ctx = useContext(BasedFinderContext)
  if (!ctx) throw new Error('missing finder ctx')

  const {
    schemaType,
    schema,
    variant,
    filter,
    activeColumns,
    availableColumns,
    sort,
    search,
    rowHeight,
    colHidden,
    allColumns,
    colVisibility,
    throttleDelay,
    colVisibilityOptions,
    setSort,
    setFilter,
    setSearch,
    setRowHeight,
    setColVisibility,
    resetView,
    setColHidden,
    onFilterChange,
    onSortChange,
    onSearchChange,
    hasTrailButtonInHeaderRef,
  } = ctx

  if (hasTrailButtonInHeaderRef) {
    hasTrailButtonInHeaderRef.current = !!trailButton
  }

  const appHeaderVariant = variant === 'modal' ? 'main' : 'sub'

  const TrailButtonWrapper = ({
    component,
    ...rest
  }: {
    component: ReactNode
    [key: string]: any
  }) => {
    if (React.isValidElement(component)) {
      return React.cloneElement(component, rest)
    }
    return component
  }

  return (
    <AppHeader variant={appHeaderVariant}>
      <AppHeader.Left>
        {leadButton && leadButton}

        <Stack fitContent style={{ marginLeft: leadButton ? 4 : 6 }}>
          <Text
            selectableText={false}
            variant="display-medium"
            color="neutral80"
            singleLine
          >
            {title || capitalize(schemaType)}
          </Text>
        </Stack>
      </AppHeader.Left>

      <AppHeader.Right>
        <Stack justify="end" gap={4}>
          {(filterButtonProp || sortButtonProp) && (
            <Stack fitContent justify="start" gap={4}>
              {filterButtonProp && (
                <FilterInput
                  schema={schema}
                  schemaType={schemaType}
                  value={filter}
                  onApply={(filter) => {
                    onFilterChange?.(filter)
                    setFilter(filter)
                  }}
                />
              )}
              {sortButtonProp && (
                <SortInput
                  columns={availableColumns}
                  sort={sort}
                  onChange={(sort) => {
                    onSortChange?.(sort)
                    setSort(sort)
                  }}
                />
              )}
            </Stack>
          )}
        </Stack>

        <Stack justify="end" gap={4}>
          {searchButtonProp && (
            <SearchInput
              value={search}
              throttleDelay={throttleDelay}
              onChange={(search) => {
                onSearchChange?.(search)
                setSearch(search)
              }}
            />
          )}

          {viewSettingsButtonProp && (
            <Menu>
              <Menu.Trigger>
                <IconButton icon="sheet" tooltip="View settings" />
              </Menu.Trigger>
              <Menu.Items>
                <Menu.Header>Columns</Menu.Header>

                <Menu.Item
                  keepOpenOnClick
                  disabled={!colVisibilityOptions.fit}
                  selected={colVisibility === 'fit'}
                  indentContent={colVisibility !== 'fit'}
                  onClick={() => setColVisibility('fit')}
                >
                  Fit to screen
                </Menu.Item>

                <Menu.Item
                  keepOpenOnClick
                  disabled={!colVisibilityOptions.scroll}
                  selected={colVisibility === 'scroll'}
                  indentContent={colVisibility !== 'scroll'}
                  onClick={() => setColVisibility('scroll')}
                >
                  Scroll horizontally
                </Menu.Item>

                <Menu.Separator />

                <Menu.Header>Rows</Menu.Header>
                <Menu.Item
                  keepOpenOnClick
                  selected={rowHeight === 'regular'}
                  indentContent={rowHeight !== 'regular'}
                  onClick={() => setRowHeight('regular')}
                >
                  Regular
                </Menu.Item>
                <Menu.Item
                  keepOpenOnClick
                  selected={rowHeight === 'large'}
                  indentContent={rowHeight !== 'large'}
                  onClick={() => setRowHeight('large')}
                >
                  Large
                </Menu.Item>

                <Menu.Separator />

                <Menu position="right">
                  <Menu.Trigger>
                    <Menu.Item
                      keepOpenOnClick
                      leadIcon="show"
                      trailIcon="chevron-right"
                      label={`Visible fields (${activeColumns.length})`}
                      onClick={() => {}}
                    />
                  </Menu.Trigger>
                  <Menu.Items>
                    <Menu.Header>Visible fields</Menu.Header>
                    {allColumns.map(({ key }, index) => (
                      <Menu.Item
                        keepOpenOnClick
                        key={index}
                        selected={!colHidden.has(key)}
                        indentContent={colHidden.has(key)}
                        onClick={() => {
                          setColHidden((prev) => {
                            const next = new Set(prev)
                            if (next.has(key)) {
                              next.delete(key)
                            } else {
                              if (activeColumns.length < 2) return next
                              next.add(key)
                            }
                            return next
                          })
                        }}
                      >
                        {capitalize(splitByUppercase(key))}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Menu>

                <Menu.Separator />

                <Menu.Item
                  keepOpenOnClick
                  label="Reset view"
                  leadIcon="revert"
                  onClick={() => resetView()}
                />
              </Menu.Items>
            </Menu>
          )}

          {trailButton && (
            <span style={{ marginLeft: 8 }}>
              <TrailButtonWrapper
                component={trailButton}
                disabled={Boolean(!activeColumns.length)}
              />
            </span>
          )}
        </Stack>
      </AppHeader.Right>
    </AppHeader>
  )
}

const BasedFinderBody: React.FC = () => {
  const ctx = useContext(BasedFinderContext)
  if (!ctx) throw new Error('missing finder ctx')

  const {
    data,
    schema,
    schemaType,
    variant,
    selected,
    disabledRows,
    maxSelectedRows,
    rowActions,
    bulkActions,
    checksum,
    sort,
    rowHeight,
    colVisibility,
    colHidden,
    disabled,
    selectableText,
    tableRef,
    checkboxVisibility,
    actionsVisibility,
    hasTrailButtonInHeaderRef,
    setColVisibility,
    setColVisibilityOptions,
    setSelectedRows,
    onSelectedRowsChange,
    setActiveColumns: setColumns,
    setColHidden,
    setSort,
    onSortChange,
    onRowClick,
    onRowSelect,
    onAddNewItem,
    pagination,
  } = ctx

  const hasTrail = Boolean(hasTrailButtonInHeaderRef?.current)

  const handleSelectedRowsChange = useCallback(
    (select: Set<number>) => {
      onSelectedRowsChange?.(select)
      setSelectedRows(select)
    },
    [onSelectedRowsChange, setSelectedRows],
  )

  const handleSortChange = useCallback(
    (next: Sort) => {
      onSortChange?.(next)
      setSort(next)
    },
    [onSortChange, setSort],
  )

  const handleColVisChange = useCallback(
    (options: Record<'fit' | 'scroll', boolean>) => {
      setColVisibility(options.fit ? 'fit' : 'scroll')
      setColVisibilityOptions(options)
    },
    [setColVisibility, setColVisibilityOptions],
  )

  return (
    <Stack
      direction="column"
      grow
      style={{
        width: '100%',
        borderRadius: variant === 'modal' ? `none` : radius[20],
        border: variant === 'modal' ? `none` : `1px solid ${colors.neutral10}`,
        background:
          variant === 'modal'
            ? `none`
            : `linear-gradient(${colors.white5}), linear-gradient(${colors.neutralInverted100})`,
        boxShadow:
          variant === 'modal' ? `none` : `0px 8px 12px ${colors.black10}`,
      }}
    >
      <Table
        ref={tableRef}
        data={data}
        checksum={checksum}
        schema={schema}
        schemaType={schemaType}
        selectedRows={selected}
        onSelectedRowsChange={handleSelectedRowsChange}
        showHideColumnButton
        bulkActions={bulkActions}
        rowActions={rowActions}
        sort={sort}
        disabled={disabled}
        rowHeight={rowHeight}
        colVisibility={colVisibility}
        colHidden={colHidden}
        selectableText={selectableText}
        onSortChange={handleSortChange}
        checkboxVisibility={checkboxVisibility}
        actionsVisibility={actionsVisibility}
        disabledRows={disabledRows}
        maxSelectedRows={maxSelectedRows}
        onRowClick={onRowClick}
        onRowSelect={onRowSelect}
        onAddNewItem={hasTrail ? null : onAddNewItem}
        onColumnsChange={setColumns}
        onColumnHidden={setColHidden}
        onColumnVisibilityChange={handleColVisChange}
        pagination={pagination}
      />
    </Stack>
  )
}

BasedFinder.Header = BasedFinderHeader
BasedFinder.Body = BasedFinderBody

export { BasedFinder }
